import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bell,
  Bold as BoldIcon,
  Code as CodeIcon,
  Highlighter,
  Image as ImageIcon,
  Italic as ItalicIcon,
  Link as LinkIcon,
  List,
  ListOrdered,
  LogOut,
  Menu,
  Redo,
  Strikethrough as StrikethroughIcon,
  Underline as UnderlineIcon,
  Undo,
  X,
  ChevronDown,
} from "lucide-react";

export default function MinistryCommunique() {
  const [ident, setIdent] = useState<string>("Ministère");
  useEffect(() => {
    try {
      const raw = localStorage.getItem("ministry.user");
      if (raw) setIdent(JSON.parse(raw)?.ident || "Ministère");
    } catch {}
  }, []);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifTab, setNotifTab] = useState<"all" | "unread">("all");
  const [notifs, setNotifs] = useState<Array<{ id: string; text: string; time: string; read: boolean }>>([]);
  const unread = notifs.filter((n) => !n.read).length;
  useEffect(() => {
    try {
      const nraw = localStorage.getItem("ministry.notifications");
      if (nraw) setNotifs(JSON.parse(nraw));
    } catch {}
  }, []);

  const editorRef = useRef<HTMLDivElement>(null);
  const [canShare, setCanShare] = useState(false);
  const savedRangeRef = useRef<Range | null>(null);
  const restoreSelection = () => {
    const sel = window.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    if (savedRangeRef.current) sel.addRange(savedRangeRef.current);
  };
  const exec = (cmd: string, val?: string) => {
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand(cmd, false, val);
    setCanShare(Boolean(editorRef.current?.innerText.trim()));
  };

  const applyHeading = (level: number | null) => {
    if (level) exec("formatBlock", "H" + level);
    else exec("formatBlock", "P");
  };

  const setAlign = (mode: "left" | "center" | "right" | "justify") => exec("justify" + mode.charAt(0).toUpperCase() + mode.slice(1));

  const [currentColor, setCurrentColor] = useState<string>("#000000");
  const setColor = (color: string) => {
    setCurrentColor(color);
    editorRef.current?.focus();
    restoreSelection();
    try {
      document.execCommand("styleWithCSS", false, "true");
    } catch {}
    document.execCommand("foreColor", false, color);
    setOpenColor(false);
    setCanShare(Boolean(editorRef.current?.innerText.trim()));
  };
  const [openHeadings, setOpenHeadings] = useState(false);
  const [openAlign, setOpenAlign] = useState(false);
  const [openColor, setOpenColor] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [targets, setTargets] = useState<{ citoyens: boolean; personnels: boolean; ministere: boolean }>({
    citoyens: true,
    personnels: true,
    ministere: true,
  });

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-12">
      <aside className="hidden md:flex md:col-span-3 lg:col-span-2 border-r bg-white p-4 md:p-6 flex-col" style={{ borderColor: "#E5E7EB" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-[#0B1F16]">{ident || "Ministère"}</div>
            <ChevronDown size={16} className="text-gray-600" />
          </div>
          <button aria-label="Notifications" className="text-[#0B1F16]">
            <Bell size={18} />
          </button>
        </div>
        <div className="flex-1 flex flex-col justify-center">
          <nav className="space-y-1">
            <NavLink
              to="/ministere/dashboard"
              className={({ isActive }) =>
                `block px-3 py-2 rounded-[8px] text-sm ${
                  isActive ? "text-[#00553E] font-semibold border-l-4 border-[#00553E]" : "text-[#0B1F16] hover:bg-[#F3F4F4]"
                }`
              }
            >
              Tableau de board
            </NavLink>
            <NavLink
              to="/ministere/communique"
              className={({ isActive }) =>
                `block px-3 py-2 rounded-[8px] text-sm ${
                  isActive ? "text-[#00553E] font-semibold border-l-4 border-[#00553E]" : "text-[#0B1F16] hover:bg-[#F3F4F4]"
                }`
              }
            >
              Lancer un communiqué
            </NavLink>
          </nav>
        </div>
        <button
          onClick={() => {
            localStorage.removeItem("ministry.user");
            location.href = "/ministere/connexion";
          }}
          className="mt-auto flex items-center gap-2 text-red-500 font-semibold text-sm"
        >
          Deconnexion
          <LogOut size={16} />
        </button>
      </aside>

      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNavOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 bg-white border-r p-4 flex flex-col" style={{ borderColor: "#E5E7EB" }}>
            <div className="flex items-center justify-between">
              <div className="font-semibold text-[#0B1F16]">{ident || "Ministère"}</div>
              <button aria-label="Fermer" className="text-[#0B1F16]" onClick={() => setMobileNavOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="mt-4">
              <nav className="space-y-1">
                <NavLink
                  to="/ministere/dashboard"
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-[8px] text-sm ${
                      isActive ? "text-[#00553E] font-semibold border-l-4 border-[#00553E]" : "text-[#0B1F16] hover:bg-[#F3F4F4]"
                    }`
                  }
                  onClick={() => setMobileNavOpen(false)}
                >
                  Tableau de board
                </NavLink>
                <NavLink
                  to="/ministere/communique"
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-[8px] text-sm ${
                      isActive ? "text-[#00553E] font-semibold border-l-4 border-[#00553E]" : "text-[#0B1F16] hover:bg-[#F3F4F4]"
                    }`
                  }
                  onClick={() => setMobileNavOpen(false)}
                >
                  Lancer un communiqué
                </NavLink>
              </nav>
            </div>
            <button
              onClick={() => {
                localStorage.removeItem("ministry.user");
                location.href = "/ministere/connexion";
              }}
              className="mt-auto flex items-center gap-2 text-red-500 font-semibold text-sm"
            >
              <span>Deconnexion</span>
              <LogOut size={16} />
            </button>
          </div>
        </div>
      )}

      <main className="md:col-span-9 lg:col-span-10 bg-[#F3F3F2]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
          <div className="md:hidden relative flex items-center justify-between mb-3">
            <button
              aria-label="Menu"
              className="h-9 w-9 rounded-[10px] border flex items-center justify-center text-[#0B1F16]"
              style={{ borderColor: "#D1E2DD" }}
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu size={18} />
            </button>
            <button aria-label="Notifications" className="text-[#0B1F16] relative" onClick={() => setNotifOpen((v) => !v)}>
              <Bell size={18} />
              {unread > 0 && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500" />}
            </button>
            {notifOpen && (
              <div className="absolute right-0 top-10 z-50 w-72 max-w-[90vw] bg-white rounded-[10px] border shadow" style={{ borderColor: "#E5E7EB" }}>
                <div className="px-5 pt-4 pb-3">
                  <div className="text-center font-semibold">Centre de notifications</div>
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <div className="flex gap-6">
                      <button
                        className={`${notifTab === "all" ? "font-semibold text-[#0B1F16] border-b-2" : "text-gray-500"}`}
                        style={{ borderColor: "#0B1F16" }}
                        onClick={() => setNotifTab("all")}
                      >
                        Toutes
                      </button>
                      <button
                        className={`${notifTab === "unread" ? "font-semibold text-[#0B1F16] border-b-2" : "text-gray-500"}`}
                        style={{ borderColor: "#0B1F16" }}
                        onClick={() => setNotifTab("unread")}
                      >
                        Non lues
                      </button>
                    </div>
                    <button
                      onClick={() => setNotifs((prev) => prev.map((n) => ({ ...n, read: true })))}
                      className={`font-semibold ${unread ? "text-[#0B9D78]" : "text-gray-300"}`}
                      disabled={!unread}
                    >
                      Tout marquer comme lues
                    </button>
                  </div>
                </div>
                <div className="border-t" style={{ borderColor: "#E5E7EB" }} />
                <div className="max-h-[360px] overflow-auto">
                  {(() => {
                    const list = notifs.filter((n) => (notifTab === "all" ? true : !n.read));
                    if (list.length === 0) {
                      return <div className="px-5 py-10 text-center text-gray-600">Vous n’avez aucune notification.</div>;
                    }
                    return list.map((n) => (
                      <div key={n.id} className={`px-5 py-3 text-sm ${!n.read ? "bg-[#DCEFE9]" : ""}`}>
                        <div className="text-gray-800">{n.text}</div>
                        <div className="mt-1 text-xs text-gray-500">{n.time}</div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}
          </div>
          <div className="text-2xl md:text-4xl font-semibold" style={{ color: "#0B1F16" }}>
            Lancer un communiqué
          </div>

          <div className="mt-4 md:mt-6 bg-[#E9ECEB] rounded-[12px] px-3 md:px-4 pr-4 md:pr-5 py-2 md:py-2.5 flex flex-wrap items-start content-start gap-x-3 gap-y-2 md:gap-5 text-[#0B1F16] text-[13px] md:text-[15px] overflow-hidden">
            <button title="Annuler" onClick={() => exec("undo")}>
              <Undo size={18} />
            </button>
            <button title="Rétablir" onClick={() => exec("redo")}>
              <Redo size={18} />
            </button>

            <div className="flex-1 flex justify-center">
              <div className="relative">
                <button
                  className="px-2 py-1 rounded font-medium"
                  onClick={() => {
                    setOpenHeadings((v) => !v);
                    setOpenAlign(false);
                    setOpenColor(false);
                  }}
                >
                  Normal text ▾
                </button>
                {openHeadings && (
                  <div className="absolute mt-2 w-40 bg-white border rounded shadow" id="dd-headings">
                    <div className="px-3 py-2 hover:bg-gray-100 cursor-pointer" onClick={() => applyHeading(null)}>
                      Normal text
                    </div>
                    <div className="px-3 py-2 hover:bg-gray-100 cursor-pointer font-semibold" onClick={() => applyHeading(1)}>
                      Heading 1
                    </div>
                    <div className="px-3 py-2 hover:bg-gray-100 cursor-pointer font-semibold" onClick={() => applyHeading(2)}>
                      Heading 2
                    </div>
                    <div className="px-3 py-2 hover:bg-gray-100 cursor-pointer font-semibold" onClick={() => applyHeading(3)}>
                      Heading 3
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="ml-auto flex items-center gap-3 md:gap-4 w-full md:w-auto justify-end pt-2 md:pt-0">
              <div className="relative">
                <button
                  className="px-1"
                  onClick={() => {
                    setOpenAlign((v) => !v);
                    setOpenHeadings(false);
                    setOpenColor(false);
                  }}
                >
                  <List size={18} />
                </button>
                {openAlign && (
                  <div className="absolute mt-2 w-32 bg-white border rounded shadow" id="dd-align">
                    <div className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2" onClick={() => setAlign("left")}>
                      <AlignLeft size={16} /> left
                    </div>
                    <div className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2" onClick={() => setAlign("center")}>
                      <AlignCenter size={16} /> center
                    </div>
                    <div className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2" onClick={() => setAlign("right")}>
                      <AlignRight size={16} /> right
                    </div>
                    <div className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2" onClick={() => setAlign("justify")}>
                      <AlignJustify size={16} /> justify
                    </div>
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  className="w-8 h-8 rounded"
                  style={{ background: currentColor }}
                  onClick={() => {
                    setOpenColor((v) => !v);
                    setOpenHeadings(false);
                    setOpenAlign(false);
                  }}
                />
                {openColor && (
                  <div className="absolute mt-2 bg-white border rounded-lg shadow p-3 w-[320px] md:w-[400px]" id="dd-color">
                    <div className="grid grid-cols-8 gap-3">
                      {[
                        "#000000",
                        "#8B5CF6",
                        "#3B82F6",
                        "#60A5FA",
                        "#22C55E",
                        "#F59E0B",
                        "#F472B6",
                        "#D1D5DB",
                        "#6D28D9",
                        "#4F46E5",
                        "#0EA5E9",
                        "#0F766E",
                        "#D97706",
                        "#B91C1C",
                      ].map((c) => (
                        <button
                          key={c}
                          className="w-9 h-9 rounded relative"
                          style={{ background: c }}
                          onClick={() => setColor(c)}
                          aria-label={`color-${c}`}
                        >
                          {currentColor === c && <span className="absolute inset-0 flex items-center justify-center text-white text-[11px]">✓</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button className="px-1" onClick={() => exec("bold")}>
                <BoldIcon size={18} />
              </button>
              <button className="px-1" onClick={() => exec("italic")}>
                <ItalicIcon size={18} />
              </button>
              <button className="px-1" onClick={() => exec("underline")}>
                <UnderlineIcon size={18} />
              </button>
              <button className="px-1" onClick={() => exec("strikeThrough")}>
                <StrikethroughIcon size={18} />
              </button>
              <button className="px-1" onClick={() => exec("insertText", "`code`")}>
                <CodeIcon size={18} />
              </button>
              <button
                className="px-1"
                onClick={() => {
                  restoreSelection();
                  document.execCommand("backColor", false, "#FFF59D");
                }}
              >
                <Highlighter size={18} />
              </button>
              <button className="px-1" onClick={() => exec("insertUnorderedList")}>
                <List size={18} />
              </button>
              <button className="px-1" onClick={() => exec("insertOrderedList")}>
                <ListOrdered size={18} />
              </button>
            </div>

            <div className="ml-auto flex items-center gap-4">
              <button
                className="px-1"
                onClick={() => {
                  const url = prompt("Lien URL");
                  if (url) exec("createLink", url);
                }}
              >
                <LinkIcon size={18} />
              </button>
              <button
                className="px-1"
                onClick={() => {
                  const url = prompt("Image URL");
                  if (url) exec("insertImage", url);
                }}
              >
                <ImageIcon size={18} />
              </button>
            </div>
          </div>

          <div
            ref={editorRef}
            className="mt-3 bg-white rounded-[12px] min-h-[420px] border p-5 text-[#0B1F16]"
            style={{ borderColor: "#E5E7EB" }}
            contentEditable
            onInput={() => setCanShare(Boolean(editorRef.current?.innerText.trim()))}
            onMouseUp={() => {
              const sel = window.getSelection();
              if (sel && sel.rangeCount > 0) savedRangeRef.current = sel.getRangeAt(0);
            }}
            onKeyUp={() => {
              const sel = window.getSelection();
              if (sel && sel.rangeCount > 0) savedRangeRef.current = sel.getRangeAt(0);
            }}
          />

          <div className="mt-6 flex justify-end">
            <button
              disabled={!canShare}
              onClick={() => setShareOpen(true)}
              className={`h-11 px-6 rounded-[10px] font-semibold ${canShare ? "text-white" : ""}`}
              style={{ background: canShare ? "#00553E" : "#DCEFE9", color: canShare ? "#FFFFFF" : "#7aa79a" }}
            >
              Partager
            </button>
          </div>

          {shareOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShareOpen(false)}>
              <div className="w-[560px] max-w-[95vw] bg-white rounded-[12px] border shadow" style={{ borderColor: "#E5E7EB" }} onClick={(e) => e.stopPropagation()}>
                <div className="p-5 border-b" style={{ borderColor: "#E5E7EB" }}>
                  <div className="font-semibold text-[#0B1F16]">Partager ce communiqué</div>
                </div>
                <div className="p-5">
                  <div className="text-gray-500 mb-4">Ce communiqué sera envoyé aux :</div>
                  <div className="space-y-3 text-[#0B1F16]">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={targets.citoyens}
                        onChange={(event) => setTargets((t) => ({ ...t, citoyens: event.target.checked }))}
                        className="w-4 h-4"
                      />
                      <span>Citoyens inscrits sur la plateforme</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={targets.personnels}
                        onChange={(event) => setTargets((t) => ({ ...t, personnels: event.target.checked }))}
                        className="w-4 h-4"
                      />
                      <span>Personnels medical</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={targets.ministere}
                        onChange={(event) => setTargets((t) => ({ ...t, ministere: event.target.checked }))}
                        className="w-4 h-4"
                      />
                      <span>Membres du ministère</span>
                    </label>
                  </div>
                </div>
                <div className="px-5 py-4 flex items-center justify-between border-t" style={{ borderColor: "#E5E7EB" }}>
                  <button onClick={() => setShareOpen(false)} className="h-10 px-4 rounded-[10px] text-[#00553E]">
                    Annuler
                  </button>
                  <button
                    disabled={!(targets.citoyens || targets.personnels || targets.ministere)}
                    onClick={() => {
                      const parts: string[] = [];
                      if (targets.citoyens) parts.push("Citoyens");
                      if (targets.personnels) parts.push("Personnels médical");
                      if (targets.ministere) parts.push("Membres du ministère");
                      const dest = parts.join(", ");
                      try {
                        const html = editorRef.current?.innerHTML || "";
                        const rawCommuniques = localStorage.getItem("ministry.communiques");
                        const list = rawCommuniques ? JSON.parse(rawCommuniques) : [];
                        const next = [
                          { id: (crypto as any).randomUUID?.() || String(Date.now()), html, recipients: parts, createdAt: Date.now() },
                          ...(Array.isArray(list) ? list : []),
                        ];
                        localStorage.setItem("ministry.communiques", JSON.stringify(next));
                      } catch {}
                      try {
                        const raw = localStorage.getItem("ministry.notifications");
                        const list = raw ? JSON.parse(raw) : [];
                        const next = [
                          {
                            id: (crypto as any).randomUUID?.() || String(Date.now()),
                            text: `Communiqué publié (destinataires: ${dest})`,
                            time: "à l'instant",
                            read: false,
                          },
                          ...(Array.isArray(list) ? list : []),
                        ];
                        localStorage.setItem("ministry.notifications", JSON.stringify(next));
                      } catch {}
                      if (editorRef.current) editorRef.current.innerHTML = "";
                      setCanShare(false);
                      setShareOpen(false);
                    }}
                    className={`h-10 px-6 rounded-[10px] font-semibold ${targets.citoyens || targets.personnels || targets.ministere ? "text-white" : ""}`}
                    style={{
                      background: targets.citoyens || targets.personnels || targets.ministere ? "#00553E" : "#DCEFE9",
                      color: targets.citoyens || targets.personnels || targets.ministere ? "#FFFFFF" : "#7aa79a",
                    }}
                  >
                    Envoyer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

